import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HubConnectionState } from "@microsoft/signalr";
import {
    ArrowUpRight, Circle, Crosshair, Download, Eraser, Hand, HardDriveUpload, Highlighter, Loader2, Lock, LockOpen,
    Maximize, Minus, MousePointer2, PencilLine, Presentation, Redo2, Square, StickyNote, Trash2, Type, Undo2, X,
    ZoomIn, ZoomOut
} from "@fg/icons";
import { useAuth } from "react-oidc-context";
import { MeetingsEvents, type UseMeetingsHub } from "@/api/useMeetingsHub";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import {
    BoardStore, HIGHLIGHT_COLORS, INK, NOTE_COLORS, PEN_COLORS, PREVIEW_STROKE_NUMBERS, boardFileName, chunkOps,
    renderBoardPng, thinStroke, wireObject, type View, type WbObject, type WbOp
} from "./boardModel";
import { WhiteboardCanvas, type Tool, type WhiteboardCanvasHandle } from "./WhiteboardCanvas";
import { downloadBlob, saveBoardToDrive, type BoardDriveSave } from "./whiteboardExport";
import { useIsDark } from "./useIsDark";

type Palette = "pen" | "highlighter" | "shape" | "text" | "note";

const TOOLS: ReadonlyArray<{ id: Tool; label: string; key: string; Icon: typeof PencilLine }> = [
    { id: "select", label: "Select and move", key: "V", Icon: MousePointer2 },
    { id: "hand", label: "Pan", key: "H", Icon: Hand },
    { id: "pen", label: "Pen", key: "P", Icon: PencilLine },
    { id: "highlighter", label: "Highlighter", key: "I", Icon: Highlighter },
    { id: "eraser", label: "Eraser", key: "E", Icon: Eraser },
    { id: "rect", label: "Rectangle", key: "R", Icon: Square },
    { id: "ellipse", label: "Ellipse", key: "O", Icon: Circle },
    { id: "arrow", label: "Arrow", key: "A", Icon: ArrowUpRight },
    { id: "line", label: "Line", key: "L", Icon: Minus },
    { id: "text", label: "Text", key: "T", Icon: Type },
    { id: "note", label: "Sticky note", key: "N", Icon: StickyNote }
];

const SIZES: Record<Exclude<Palette, "note">, { label: string; values: number[] }> = {
    pen: { label: "Thickness", values: [2, 4, 8, 14] },
    highlighter: { label: "Thickness", values: [12, 20, 32] },
    shape: { label: "Thickness", values: [2, 4, 8] },
    text: { label: "Text size", values: [16, 24, 36, 56] }
};

function paletteFor(tool: Tool): Palette | null {
    switch (tool) {
        case "pen": return "pen";
        case "highlighter": return "highlighter";
        case "rect": case "ellipse": case "arrow": case "line": return "shape";
        case "text": return "text";
        case "note": return "note";
        default: return null;
    }
}

interface StatePayload {
    reason?: string;
    byDisplayName?: string | null;
    byConnectionId?: string | null;
    locked?: boolean;
    leaderConnectionId?: string | null;
    leaderName?: string | null;
    viewers?: number;
    drawers?: string[] | null;
}

function reasonText(reason: string | null | undefined): string {
    switch (reason) {
        case "locked": return "Only presenters can draw right now.";
        case "rate": return "You're drawing faster than the board can keep up — some changes were dropped.";
        case "full": return "The whiteboard is full. Erase something to make room.";
        case "invalid": return "Something couldn't be added to the whiteboard.";
        default: return "The whiteboard lost track of a change — it has been reloaded.";
    }
}

/**
 * Teams-style meeting whiteboard: a shared board for everyone in the room
 * (guests too, unless the organizer turns on "Only presenters can draw").
 * Strokes and objects go over the meetings hub as small JSON ops; the server
 * keeps the full board and hands it to late joiners. Undo/redo covers your
 * own changes. "Save to Drive" puts a PNG in your Drive's Recordings folder
 * shared with the meeting; "Download" saves it locally.
 */
export function MeetingWhiteboard({ meetingId, meetingSubject, hub, isGuest, onClose }: {
    meetingId: string;
    meetingSubject: string;
    hub: UseMeetingsHub;
    isGuest: boolean;
    onClose: () => void;
}) {
    const token = useAccessToken();
    const auth = useAuth();
    const dark = useIsDark();
    const store = useMemo(() => new BoardStore(), []);
    const canvasRef = useRef<WhiteboardCanvasHandle>(null);
    const hubRef = useRef(hub);
    hubRef.current = hub;

    const [status, setStatus] = useState<"connecting" | "ready" | "error">("connecting");
    const [isHost, setIsHost] = useState(false);
    const [locked, setLocked] = useState(false);
    const [drawers, setDrawers] = useState<string[]>([]);
    const [leader, setLeader] = useState<{ connectionId: string; name: string } | null>(null);
    const [following, setFollowing] = useState(true);
    const [viewers, setViewers] = useState(1);
    const [tool, setTool] = useState<Tool>("pen");
    const [colors, setColors] = useState<Record<Palette, string>>({
        pen: INK, highlighter: HIGHLIGHT_COLORS[0]!.value, shape: INK, text: INK, note: NOTE_COLORS[0]!.value
    });
    const [sizes, setSizes] = useState<Record<Exclude<Palette, "note">, number>>({ pen: 4, highlighter: 20, shape: 4, text: 24 });
    const [zoomPct, setZoomPct] = useState(100);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState<"drive" | "png" | null>(null);
    const [driveResult, setDriveResult] = useState<BoardDriveSave | null>(null);
    const [confirmClear, setConfirmClear] = useState(false);
    const [selectionCount, setSelectionCount] = useState(0);
    const [empty, setEmpty] = useState(true);
    const undoRef = useRef<Array<{ ops: WbOp[]; undo: WbOp[] }>>([]);
    const redoRef = useRef<Array<{ ops: WbOp[]; undo: WbOp[] }>>([]);
    const [, setHistoryTick] = useState(0);

    const myConn = hub.connectionId;
    const canDraw = status === "ready" && (!locked || (myConn !== null && drawers.includes(myConn)));
    const amLeader = leader !== null && leader.connectionId === myConn;

    // Handlers below are stable; these carry the latest values into them.
    const live = useRef({ canDraw, leader, following, myConn, viewers });
    live.current = { canDraw, leader, following, myConn, viewers };

    useEffect(() => store.subscribe(() => setEmpty(store.size === 0)), [store]);

    // Notices fade on their own.
    useEffect(() => {
        if (!notice) return;
        const t = window.setTimeout(() => setNotice(null), 6000);
        return () => window.clearTimeout(t);
    }, [notice]);

    // ── open / resync ────────────────────────────────────────────────────
    const firstLoad = useRef(true);
    const openBoard = useCallback(async (): Promise<boolean> => {
        const r = await hubRef.current.whiteboardOpen();
        if (!r) return false;
        store.load(r.board.objects ?? []);
        setLocked(r.board.locked);
        setDrawers(r.drawers ?? []);
        setIsHost(r.isHost);
        setViewers(Math.max(1, r.board.viewers));
        setLeader(r.board.leader ? { connectionId: r.board.leader.connectionId, name: r.board.leader.displayName } : null);
        setStatus("ready");
        if (firstLoad.current) {
            firstLoad.current = false;
            requestAnimationFrame(() => canvasRef.current?.fit());
        }
        return true;
    }, [store]);

    const resyncing = useRef(false);
    const resync = useCallback(async () => {
        if (resyncing.current) return;
        resyncing.current = true;
        try { await openBoard(); } finally { resyncing.current = false; }
    }, [openBoard]);

    // (Re)open whenever the hub (re)connects — the server forgets viewers on a drop.
    useEffect(() => {
        if (hub.state !== HubConnectionState.Connected) {
            setStatus((s) => (s === "ready" ? "connecting" : s));
            return;
        }
        let cancelled = false;
        let timer: number | undefined;
        const attempt = async (n: number) => {
            const ok = await openBoard();
            if (cancelled || ok) return;
            // Not in the room yet (still joining after a reconnect) — try again shortly.
            if (n < 6) timer = window.setTimeout(() => void attempt(n + 1), 1500);
            else setStatus("error");
        };
        setStatus((s) => (s === "error" ? "connecting" : s));
        void attempt(0);
        return () => {
            cancelled = true;
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [hub.state, hub.connectionId, openBoard]);

    // Closing the board tells the room.
    useEffect(() => () => { void hubRef.current.whiteboardClose(); }, []);

    // ── hub events ───────────────────────────────────────────────────────
    useEffect(() => {
        const offs = [
            hub.on(MeetingsEvents.WhiteboardOps, (payload) => {
                const p = payload as { connectionId?: string; ops?: WbOp[] };
                if (p.connectionId) store.setPreview(p.connectionId, "", null);
                if (Array.isArray(p.ops)) store.apply(p.ops);
            }),
            hub.on(MeetingsEvents.WhiteboardPreview, (payload) => {
                const p = payload as { connectionId?: string; displayName?: string; obj?: WbObject | null };
                if (p.connectionId) store.setPreview(p.connectionId, p.displayName ?? "", p.obj ?? null);
            }),
            hub.on(MeetingsEvents.WhiteboardView, (payload) => {
                const p = payload as { connectionId?: string; centerX?: number; centerY?: number; zoom?: number };
                const l = live.current;
                if (!l.following || !l.leader || p.connectionId !== l.leader.connectionId || l.leader.connectionId === l.myConn) return;
                if (typeof p.centerX === "number" && typeof p.centerY === "number" && typeof p.zoom === "number") {
                    canvasRef.current?.setCenter(p.centerX, p.centerY, p.zoom);
                }
            }),
            hub.on(MeetingsEvents.WhiteboardState, (payload) => {
                const p = payload as StatePayload;
                if (typeof p.locked === "boolean") setLocked(p.locked);
                if (Array.isArray(p.drawers)) setDrawers(p.drawers);
                if (typeof p.viewers === "number") setViewers(Math.max(1, p.viewers));
                const next = p.leaderConnectionId ? { connectionId: p.leaderConnectionId, name: p.leaderName ?? "Someone" } : null;
                // A new leader: everyone follows by default (they can stop).
                if (next && next.connectionId !== live.current.leader?.connectionId) setFollowing(true);
                setLeader(next);
                const mine = p.byConnectionId && p.byConnectionId === live.current.myConn;
                if (p.reason === "cleared") {
                    store.load([]);
                    undoRef.current = [];
                    redoRef.current = [];
                    setHistoryTick((t) => t + 1);
                    if (!mine) setNotice(`${p.byDisplayName || "The organizer"} cleared the whiteboard.`);
                } else if (p.reason === "locked" && !mine) {
                    setNotice(p.locked ? "The organizer turned on “Only presenters can draw”." : "Everyone can draw again.");
                }
            })
        ];
        const prune = window.setInterval(() => store.prunePreviews(5000), 2000);
        return () => {
            offs.forEach((off) => off());
            window.clearInterval(prune);
        };
    }, [hub.on, store]);

    // ── sending ──────────────────────────────────────────────────────────
    const send = useCallback(async (ops: WbOp[]) => {
        const wire = ops.map((op) => (op.obj ? { op: op.op, obj: wireObject(op.obj) } : { op: op.op, ids: op.ids }));
        const { batches, dropped } = chunkOps(wire);
        if (dropped > 0) setNotice("Something was too large to add to the whiteboard.");
        for (const json of batches) {
            const r = await hubRef.current.whiteboardOps(json);
            if (!r) {
                setNotice("Reconnecting to the whiteboard…");
                return;
            }
            if (!r.ok && r.reason !== "gone") {
                setNotice(reasonText(r.reason));
                void resync();
                return;
            }
        }
    }, [resync]);

    const onCommit = useCallback((ops: WbOp[], undo: WbOp[]) => {
        if (!live.current.canDraw) return;
        store.apply(ops);
        undoRef.current.push({ ops, undo });
        if (undoRef.current.length > 200) undoRef.current.shift();
        redoRef.current = [];
        setHistoryTick((t) => t + 1);
        void send(ops);
    }, [send, store]);

    const undo = useCallback(() => {
        const entry = undoRef.current.pop();
        if (!entry || !live.current.canDraw) return;
        store.apply(entry.undo);
        redoRef.current.push(entry);
        setHistoryTick((t) => t + 1);
        void send(entry.undo);
    }, [send, store]);

    const redo = useCallback(() => {
        const entry = redoRef.current.pop();
        if (!entry || !live.current.canDraw) return;
        store.apply(entry.ops);
        undoRef.current.push(entry);
        setHistoryTick((t) => t + 1);
        void send(entry.ops);
    }, [send, store]);

    // In-progress strokes go out ~11 times a second, thinned, and only when someone else is watching.
    const previewTimer = useRef<number | null>(null);
    const pendingPreview = useRef<WbObject | null>(null);
    const lastPreviewAt = useRef(0);
    const onPreview = useCallback((obj: WbObject | null) => {
        if (!obj) {
            const hadPending = pendingPreview.current !== null || lastPreviewAt.current > 0;
            pendingPreview.current = null;
            if (previewTimer.current !== null) {
                window.clearTimeout(previewTimer.current);
                previewTimer.current = null;
            }
            if (hadPending && live.current.viewers > 1) void hubRef.current.whiteboardPreview(null);
            lastPreviewAt.current = 0;
            return;
        }
        if (live.current.viewers <= 1) return;
        pendingPreview.current = obj;
        if (previewTimer.current !== null) return;
        const wait = Math.max(0, 90 - (Date.now() - lastPreviewAt.current));
        previewTimer.current = window.setTimeout(() => {
            previewTimer.current = null;
            const o = pendingPreview.current;
            if (!o) return;
            lastPreviewAt.current = Date.now();
            const thin = o.points ? { ...o, points: thinStroke(o.points, PREVIEW_STROKE_NUMBERS) } : o;
            void hubRef.current.whiteboardPreview(JSON.stringify(wireObject(thin)));
        }, wait);
    }, []);
    useEffect(() => () => { if (previewTimer.current !== null) window.clearTimeout(previewTimer.current); }, []);

    // Leading: my viewport goes to followers (≤ 10/s).
    const lastViewSent = useRef(0);
    const viewTimer = useRef<number | null>(null);
    const sendView = useCallback(() => {
        const c = canvasRef.current?.getCenter();
        if (!c) return;
        lastViewSent.current = Date.now();
        void hubRef.current.whiteboardView(c.cx, c.cy, c.zoom);
    }, []);
    const onViewChange = useCallback((v: View, byUser: boolean) => {
        setZoomPct(Math.round(v.zoom * 100));
        const l = live.current;
        if (byUser && l.following && l.leader && l.leader.connectionId !== l.myConn) setFollowing(false);
        if (l.leader && l.leader.connectionId === l.myConn) {
            if (viewTimer.current !== null) return;
            const wait = Math.max(0, 100 - (Date.now() - lastViewSent.current));
            viewTimer.current = window.setTimeout(() => {
                viewTimer.current = null;
                sendView();
            }, wait);
        }
    }, [sendView]);
    useEffect(() => () => { if (viewTimer.current !== null) window.clearTimeout(viewTimer.current); }, []);

    const toggleLead = async () => {
        if (amLeader) {
            await hub.whiteboardLead(false);
        } else {
            await hub.whiteboardLead(true);
            window.setTimeout(sendView, 150);
        }
    };

    // ── export ───────────────────────────────────────────────────────────
    const downloadPng = async () => {
        setBusy("png");
        try {
            downloadBlob(await renderBoardPng(store.list()), boardFileName(meetingSubject));
        } catch (err) {
            setNotice(err instanceof Error ? err.message : "Couldn't export the whiteboard.");
        } finally {
            setBusy(null);
        }
    };
    const saveToDrive = async () => {
        setBusy("drive");
        setDriveResult(null);
        try {
            const blob = await renderBoardPng(store.list());
            setDriveResult(await saveBoardToDrive({
                blob,
                fileName: boardFileName(meetingSubject),
                meetingId,
                myEmail: auth.user?.profile.email,
                token
            }));
        } catch (err) {
            setNotice(err instanceof Error ? `Couldn't save to Drive: ${err.message}` : "Couldn't save to Drive.");
        } finally {
            setBusy(null);
        }
    };

    // ── keyboard shortcuts (tools, undo / redo) ─────────────────────────
    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "TEXTAREA" || target.tagName === "INPUT") return;
        const mod = e.ctrlKey || e.metaKey;
        if (mod && e.key.toLowerCase() === "z") {
            e.preventDefault();
            if (e.shiftKey) redo(); else undo();
            return;
        }
        if (mod && e.key.toLowerCase() === "y") {
            e.preventDefault();
            redo();
            return;
        }
        if (mod || e.altKey || !canDraw) return;
        const t = TOOLS.find((x) => x.key.toLowerCase() === e.key.toLowerCase());
        if (t) {
            e.preventDefault();
            setTool(t.id);
        }
    };

    const palette = paletteFor(tool);
    const colorList = palette === "highlighter" ? HIGHLIGHT_COLORS : palette === "note" ? NOTE_COLORS : PEN_COLORS;
    const effectiveTool: Tool = canDraw ? tool : "hand";
    const canvasColor = palette ? colors[palette] : INK;
    const canvasSize = palette && palette !== "note" ? sizes[palette] : 16;

    const toolBtn = "flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:hover:bg-transparent";
    const headerBtn = "flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2 text-xs font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";

    return (
        <section
            className="fixed inset-0 z-50 flex flex-col bg-background text-foreground"
            aria-label="Meeting whiteboard"
            onKeyDown={onKeyDown}
        >
            <header className="flex shrink-0 flex-wrap items-center gap-2 border-b bg-card px-3 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <PencilLine className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <h2 className="truncate text-sm font-semibold">Whiteboard</h2>
                    <span className="hidden text-xs text-muted-foreground sm:inline">
                        · {viewers} {viewers === 1 ? "person" : "people"} viewing
                    </span>
                    {locked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                            <Lock className="h-3 w-3" aria-hidden /> Only presenters can draw
                        </span>
                    ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {leader && !amLeader ? (
                        <button
                            type="button"
                            onClick={() => setFollowing((f) => !f)}
                            aria-pressed={following}
                            className={cn(headerBtn, following && "border-primary bg-primary/10 text-primary")}
                            title={following ? `Stop following ${leader.name}` : `Follow ${leader.name}'s view`}
                        >
                            <Crosshair className="h-3.5 w-3.5" aria-hidden />
                            <span className="max-w-[10rem] truncate">{following ? `Following ${leader.name}` : `Follow ${leader.name}`}</span>
                        </button>
                    ) : null}
                    {canDraw ? (
                        <button
                            type="button"
                            onClick={() => void toggleLead()}
                            aria-pressed={amLeader}
                            className={cn(headerBtn, amLeader && "border-primary bg-primary/10 text-primary")}
                            title={amLeader ? "Stop leading" : "Let everyone follow your view"}
                        >
                            <Presentation className="h-3.5 w-3.5" aria-hidden />
                            <span className="hidden sm:inline">{amLeader ? "Stop leading" : "Lead view"}</span>
                        </button>
                    ) : null}
                    {isHost ? (
                        <button
                            type="button"
                            onClick={() => void hub.whiteboardLock(!locked)}
                            aria-pressed={locked}
                            className={headerBtn}
                            title={locked ? "Let everyone draw" : "Only presenters can draw"}
                        >
                            {locked ? <LockOpen className="h-3.5 w-3.5" aria-hidden /> : <Lock className="h-3.5 w-3.5" aria-hidden />}
                            <span className="hidden md:inline">{locked ? "Let everyone draw" : "Only presenters draw"}</span>
                        </button>
                    ) : null}
                    {!isGuest ? (
                        <button type="button" onClick={() => void saveToDrive()} disabled={busy !== null || empty} className={headerBtn} title="Save a picture of the board to your Drive, shared with the meeting">
                            {busy === "drive" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <HardDriveUpload className="h-3.5 w-3.5" aria-hidden />}
                            <span className="hidden sm:inline">Save to Drive</span>
                        </button>
                    ) : null}
                    <button type="button" onClick={() => void downloadPng()} disabled={busy !== null || empty} className={headerBtn} title="Download as PNG">
                        {busy === "png" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Download className="h-3.5 w-3.5" aria-hidden />}
                        <span className="hidden sm:inline">Download</span>
                    </button>
                    {isHost ? (
                        <button type="button" onClick={() => setConfirmClear(true)} disabled={empty} className={cn(headerBtn, "text-destructive")} title="Clear the board for everyone">
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                            <span className="hidden sm:inline">Clear</span>
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        aria-label="Close the whiteboard"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden /> <span className="hidden sm:inline">Close</span>
                    </button>
                </div>
            </header>

            {confirmClear ? (
                <div role="alertdialog" aria-label="Clear the whiteboard" className="flex shrink-0 flex-wrap items-center justify-center gap-3 border-b bg-destructive/10 px-4 py-2 text-sm">
                    <span>Clear the whiteboard for everyone? This can't be undone.</span>
                    <button
                        type="button"
                        autoFocus
                        onClick={() => { setConfirmClear(false); void hub.whiteboardClear(); }}
                        className="rounded-md bg-destructive px-3 py-1 text-xs font-semibold text-destructive-foreground"
                    >
                        Clear for everyone
                    </button>
                    <button type="button" onClick={() => setConfirmClear(false)} className="rounded-md border px-3 py-1 text-xs hover:bg-accent">
                        Cancel
                    </button>
                </div>
            ) : null}

            {/* Tools: one row that scrolls sideways on a phone. */}
            <div className="shrink-0 overflow-x-auto border-b bg-card">
                <div role="toolbar" aria-label="Whiteboard tools" aria-orientation="horizontal" className="flex w-max items-center gap-1 px-2 py-1.5">
                    {TOOLS.map((t) => {
                        const disabled = !canDraw && t.id !== "hand";
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTool(t.id)}
                                disabled={disabled}
                                aria-pressed={effectiveTool === t.id}
                                aria-label={t.label}
                                aria-keyshortcuts={t.key}
                                title={`${t.label} (${t.key})`}
                                className={cn(toolBtn, effectiveTool === t.id && "bg-primary text-primary-foreground hover:bg-primary")}
                            >
                                <t.Icon className="h-4 w-4" aria-hidden />
                            </button>
                        );
                    })}
                    <span className="mx-1 h-6 w-px bg-border" aria-hidden />
                    <button type="button" onClick={undo} disabled={!canDraw || undoRef.current.length === 0} aria-label="Undo" aria-keyshortcuts="Control+Z" title="Undo (Ctrl+Z)" className={toolBtn}>
                        <Undo2 className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" onClick={redo} disabled={!canDraw || redoRef.current.length === 0} aria-label="Redo" aria-keyshortcuts="Control+Y" title="Redo (Ctrl+Y)" className={toolBtn}>
                        <Redo2 className="h-4 w-4" aria-hidden />
                    </button>
                    {tool === "select" && selectionCount > 0 && canDraw ? (
                        <button type="button" onClick={() => canvasRef.current?.deleteSelection()} aria-label={`Delete ${selectionCount} selected`} title="Delete selection (Delete)" className={cn(toolBtn, "text-destructive")}>
                            <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                    ) : null}
                    {palette && canDraw ? (
                        <>
                            <span className="mx-1 h-6 w-px bg-border" aria-hidden />
                            <div role="radiogroup" aria-label={palette === "note" ? "Note colour" : "Colour"} className="flex items-center gap-1">
                                {colorList.map((c) => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={colors[palette] === c.value}
                                        aria-label={c.label}
                                        title={c.label}
                                        onClick={() => setColors((prev) => ({ ...prev, [palette]: c.value }))}
                                        className={cn(
                                            "h-6 w-6 shrink-0 rounded-full border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                                            colors[palette] === c.value ? "border-primary ring-2 ring-primary/40" : "border-border"
                                        )}
                                        style={{ backgroundColor: c.value === INK && dark ? "#f4f4f5" : c.value }}
                                    />
                                ))}
                            </div>
                            {palette !== "note" ? (
                                <>
                                    <span className="mx-1 h-6 w-px bg-border" aria-hidden />
                                    <div role="radiogroup" aria-label={SIZES[palette].label} className="flex items-center gap-0.5">
                                        {SIZES[palette].values.map((v) => (
                                            <button
                                                key={v}
                                                type="button"
                                                role="radio"
                                                aria-checked={sizes[palette] === v}
                                                aria-label={palette === "text" ? `${v} point` : `${v} pixels`}
                                                title={palette === "text" ? `${v} pt` : `${v} px`}
                                                onClick={() => setSizes((prev) => ({ ...prev, [palette]: v }))}
                                                className={cn(toolBtn, "h-8 w-8", sizes[palette] === v && "bg-accent ring-1 ring-primary")}
                                            >
                                                {palette === "text" ? (
                                                    <span className="text-[11px] font-semibold">{v}</span>
                                                ) : (
                                                    <span className="rounded-full bg-foreground" style={{ width: Math.min(18, Math.max(3, v * 0.7)), height: Math.min(18, Math.max(3, v * 0.7)) }} />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>

            <div className="relative min-h-0 flex-1">
                <WhiteboardCanvas
                    ref={canvasRef}
                    store={store}
                    dark={dark}
                    readOnly={!canDraw}
                    tool={effectiveTool}
                    color={canvasColor}
                    size={canvasSize}
                    onCommit={onCommit}
                    onPreview={onPreview}
                    onViewChange={onViewChange}
                    onSelectionChange={setSelectionCount}
                    onToolDone={() => setTool("select")}
                    className="h-full w-full"
                    ariaLabel={canDraw ? "Whiteboard canvas" : "Whiteboard canvas (view only)"}
                />

                {status !== "ready" ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                        {status === "error" ? (
                            <div role="alert" className="flex flex-col items-center gap-2 rounded-lg border bg-card px-5 py-4 text-sm shadow">
                                <span>Couldn't open the whiteboard.</span>
                                <button type="button" onClick={() => { setStatus("connecting"); void resync().then(() => setStatus((s) => (s === "ready" ? s : "error"))); }} className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                                    Try again
                                </button>
                            </div>
                        ) : (
                            <span className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Opening the whiteboard…</span>
                        )}
                    </div>
                ) : null}

                {status === "ready" && empty && canDraw ? (
                    <p className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-card/90 px-3 py-1 text-xs text-muted-foreground shadow">
                        Everyone in the meeting sees what you draw here.
                    </p>
                ) : null}
                {status === "ready" && !canDraw ? (
                    <p role="status" className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-card/90 px-3 py-1 text-xs text-muted-foreground shadow">
                        The organizer has limited drawing to presenters. You can still look around.
                    </p>
                ) : null}

                {/* Zoom */}
                <div className="absolute bottom-3 right-3 flex items-center gap-0.5 rounded-lg border bg-card p-0.5 shadow" role="group" aria-label="Zoom">
                    <button type="button" onClick={() => canvasRef.current?.zoomBy(1 / 1.2)} aria-label="Zoom out" title="Zoom out (-)" className={cn(toolBtn, "h-8 w-8")}>
                        <ZoomOut className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" onClick={() => canvasRef.current?.fit()} aria-label={`Zoom ${zoomPct}%. Fit the board`} title="Fit the board (0)" className="h-8 min-w-[3.25rem] rounded-md px-1 text-xs tabular-nums hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                        {zoomPct}%
                    </button>
                    <button type="button" onClick={() => canvasRef.current?.zoomBy(1.2)} aria-label="Zoom in" title="Zoom in (+)" className={cn(toolBtn, "h-8 w-8")}>
                        <ZoomIn className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" onClick={() => canvasRef.current?.fit()} aria-label="Fit to screen" title="Fit to screen" className={cn(toolBtn, "h-8 w-8")}>
                        <Maximize className="h-4 w-4" aria-hidden />
                    </button>
                </div>

                <div className="pointer-events-none absolute bottom-3 left-3 right-24 flex flex-col items-start gap-2">
                    {notice ? (
                        <div role="status" className="pointer-events-auto max-w-md rounded-md border bg-card px-3 py-2 text-xs shadow">
                            {notice}
                        </div>
                    ) : null}
                    {driveResult ? (
                        <div role="status" className="pointer-events-auto flex max-w-md flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 text-xs shadow">
                            <span>{driveResult.warning ?? "Saved to your Drive and shared with the meeting."}</span>
                            <a href={driveResult.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline">Open in Drive</a>
                            <button type="button" onClick={() => setDriveResult(null)} aria-label="Dismiss" className="rounded p-0.5 hover:bg-accent">
                                <X className="h-3.5 w-3.5" aria-hidden />
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
